
import React from "react";
import { Label } from "@/components/ui/label";
import NumericInput from "@/components/ui/NumericInput";
import { AlertTriangle, InfoIcon } from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

interface CreditScoreInputProps {
  creditScore: number;
  onChange: (value: number) => void;
}

const CreditScoreInput: React.FC<CreditScoreInputProps> = ({ creditScore, onChange }) => {
  // FHA loan requires at least 580 credit score for 3.5% down payment
  // or 500-579 credit score for 10% down payment
  const isValidCreditScore = creditScore >= 500;
  const needsHigherDownPayment = creditScore < 580 && creditScore >= 500;

  // Helper function to render warning if needed
  const renderCreditScoreWarning = () => {
    if (!isValidCreditScore) {
      return (
        <div className="flex items-center text-red-600 text-sm mt-1">
          <AlertTriangle className="h-4 w-4 mr-1" />
          <span>FHA loans require minimum 500 credit score</span>
        </div>
      );
    }
    
    if (needsHigherDownPayment) {
      return (
        <div className="flex items-center text-amber-600 text-sm mt-1">
          <AlertTriangle className="h-4 w-4 mr-1" />
          <span>Credit score 500-579 requires 10% down payment</span>
        </div>
      );
    }
    
    return null;
  };

  return (
    <div className="space-y-1">
      <div className="flex items-center">
        <Label htmlFor="creditScore">Credit Score</Label>
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <InfoIcon className="h-4 w-4 text-gray-400 ml-2" />
            </TooltipTrigger>
            <TooltipContent className="w-80 p-3">
              <p>FHA loans require a minimum credit score of 500. 
              With a score of 580+, you can qualify with a 3.5% down payment.
              Scores between 500-579 require a 10% down payment.</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>
      <NumericInput
        id="creditScore"
        value={creditScore}
        onChange={onChange}
        min={300}
        max={850}
        step={1}
        decimalPlaces={0}
        className={!isValidCreditScore ? "border-red-300" : ""}
      />
      {renderCreditScoreWarning()}
    </div>
  );
};

export default CreditScoreInput;
